import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Button, Spinner } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useRetentionService } from "modules/Pharmacy/Retention/api/useRetentionService";
import { showToastMessage, parseLocalDate } from "../../../../common/service/CommonService";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { RETENTION_PATHS } from "Routes/paths";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { RetentionFormValues } from "modules/Pharmacy/Retention/helpers/retention.types";
import FullPageLoader from "Components/Common/FullPageLoader";
import { validations } from "validations/FormValidations";
import {
  IP_STORAGE_CONDITION_OPTIONS,
  RETENTION_TYPE_OPTIONS,
} from "api/modules/common.option";
import { useDispensingLog } from "modules/Pharmacy/Dispensing/api/useDispensingLog";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";

import SectionCard from "Components/Common/SectionCard";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";
import { queryClient } from "api/queryClient";

const AddRetention: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = paramId || searchParams.get("id");

  document.title = `${editId ? "Edit" : "Add"} Retention Record | ZENTIXS ORBIT`;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Retention", link: RETENTION_PATHS.LIST },
      { text: editId ? "Edit Retention Record" : "Add Retention Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const { InsertMutation, UpdateMutation, SelectIdQuery } = useRetentionService();
  const selectQuery = SelectIdQuery(editId ? Number(editId) : null);

  const { DropdownQuery: studySelectAllQuery } = useDispensingLog({ getStudyDropDown: true });
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || [],
    [studySelectAllQuery.data],
  );

  useEffect(() => {
    loadCodelistsDetailId(["IP_STORAGE_CONDITION_OPTIONS", "RETENTION_TYPE_OPTIONS"]);
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<RetentionFormValues>({
    defaultValues: {
      studycode: "",
      sponsorname: "",
      ipcategory: "",
      ipid: "",
      productname: "",
      strength: "",
      dosageform: "",
      batchlotno: "",
      receivedqty: "",
      retentionqty: "",
      retentiontype: "",
      storagecondition: "",
      retentionstartdate: "",
      retentionperiod: "",
      retentionexpirydate: "",
    },
    mode: "onBlur",
  });

  const { selectedStudy: globalStudy } = useGlobalStudy();

  useEffect(() => {
    if (editId) return;
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studycode", matched.value, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } else {
      setValue("studycode", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [globalStudy?.studyid, studyOptions, editId, setValue]);

  const selectedStudyCodedetails = watch("studycode");
  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => s.value === selectedStudyCodedetails),
    [selectedStudyCodedetails, studyOptions],
  );

  const { SelectIpIDQuery: ipSelectQuery } = useIpInventoryService();
  const ipQuery = ipSelectQuery(selectedStudy?.value || null);

  const ipOptions = useMemo(
    () =>
      (ipQuery.data as any)?.data?.map((ip: any) => ({
        label: `${ip.batchlotno} (${ip.productname})`,
        value: ip.ipid,
        ...ip,
      })) || [],
    [ipQuery.data],
  );

  useEffect(() => {
    let record = selectQuery.data?.data?.[0];

    if (record) {
      reset({
        ...record,
        studycode: record?.studyid,
        receivedqty: record?.ipquantity,
        retentionqty: record?.retentionquantity,
        retentionstartdate: record.retentionstartdate ? record.retentionstartdate.split("T")[0] : "",
        retentionexpirydate: record.retentionexpirydate ? record.retentionexpirydate.split("T")[0] : "",
      });
    }
  }, [selectQuery.data, reset, editId]);

  const selectedStudyCode = watch("studycode");
  const selectedIP = watch("ipcategory");
  const startDate = watch("retentionstartdate");
  const expiryDate = watch("retentionexpirydate");
  const receivedqty = watch("receivedqty");

  // Auto-calculate period
  useEffect(() => {
    if (startDate && expiryDate) {
      const start = new Date(startDate);
      const end = new Date(expiryDate);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        setValue("retentionperiod", "Invalid Dates");
        return;
      }
      else {
        setValue("retentionperiod", `${diffDays} Day${diffDays !== 1 ? "s" : ""}`);
      }
    }
  }, [startDate, expiryDate, setValue]);
  const [createddate, setCreateddate] = useState("");
  const [expirydate, setExpirydate] = useState("");
  const [ipid, setIpid] = useState("");
  // Auto-fill logic based on study and category
  useEffect(() => {
    if (editId) return;

    if (selectedStudyCode === "" || !selectedStudyCode) {
      setValue("sponsorid", "");
      setValue("sponsorname", "");
      setValue("productname", "");
      setValue("strength", "");
      setValue("dosageform", "");
      setValue("batchlotno", "");
      setValue("storagecondition", "");
      setValue("receivedqty", "");
      setValue("retentionqty", "");
      setValue("retentiontype", "");
      setValue("retentionstartdate", "");
      setValue("retentionexpirydate", "");
      setValue("retentionperiod", "");
      setExpirydate("");
      setCreateddate("");
      setIpid("");
      return;
    }

    if (!ipOptions || ipOptions.length === 0) return;

    // Find the record matching the selected category (Test/Reference)
    const matchingIP = ipOptions.find((ip: any) => ip.ipcategory === selectedIP);

    if (matchingIP) {
      setValue("sponsorname", matchingIP.sponsorname ?? "");
      setValue("sponsorid", matchingIP.sponsorid ?? "");
      setValue("productname", matchingIP.productname ?? "");
      setValue("strength", matchingIP.strength ?? "");
      setValue("dosageform", matchingIP.dosageform ?? "");
      setValue("batchlotno", matchingIP.batchlotno ?? "");
      setValue("storagecondition", matchingIP.storagecondition ?? "");
      setValue("receivedqty", matchingIP.availablequantity ?? "");
      setExpirydate(matchingIP.expirydate ? matchingIP.expirydate : "");
      setCreateddate(matchingIP.createddate ? matchingIP.createddate.split("T")[0] : "");
      setIpid(matchingIP.ipid);
    } else {
      // Clear fields if no matching category found in inventory
      setValue("sponsorname", ipOptions[0]?.sponsorname ?? ""); // Fallback sponsor from any record
      setValue("productname", "");
      setValue("strength", "");
      setValue("dosageform", "");
      setValue("batchlotno", "");
      setValue("storagecondition", "");
    }
  }, [selectedIP, ipOptions, setValue, editId, selectedStudyCode]);

  const retentionstartdate = watch("retentionstartdate")

  const IP_CATEGORY_OPTIONS = [
    { label: "Test Product", value: "Test Product" },
    { label: "Reference Product", value: "Reference Product" },
    { label: "Placebo", value: "Placebo" },
    { label: "Ancillary / Supply", value: "Ancillary / Supply" },
  ];

  const [IPCategoryOptions, setIPCategoryOptions] = useState<any[]>(IP_CATEGORY_OPTIONS);

  useEffect(() => {
    if (!ipOptions) return;
    if (ipOptions.length > 0) {
      const uniqueCategories = ipOptions?.map((ip: any) => ip.ipcategory);
      const updatedIPCategoryOptions = uniqueCategories.map((category: string) => ({
        label: category,
        value: category
      }));
      setIPCategoryOptions(updatedIPCategoryOptions);
    }
  }, [ipOptions]);

  useEffect(() => {
    if (editId) return;

    if (!selectedStudyCode) return;

    // Always auto-select first category when study changes
    setValue("ipcategory", IPCategoryOptions[0].value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }, [selectedStudyCode, IPCategoryOptions, editId, setValue]
  );

  const section1FieldsRow1: FormFieldConfig[] = [
    {
      name: "studycode",
      label: "Study Code",
      type: "select",
      required: true,
      options: studyOptions,
      disabled: true,
      placeholder: "Select study code...",
      rules: validations.drugRetention.studyid,
    },
    {
      name: "sponsorname",
      label: "Sponsor Name",
      type: "text",
      placeholder: "Auto-filled",
      disabled: true,
    },
  ];

  const section1FieldsRow2: FormFieldConfig[] = [
    {
      name: "ipcategory",
      label: "IP Category",
      type: "radio",
      required: true,
      disabled: !selectedStudyCode || !!editId,
      options: IPCategoryOptions,
      rules: validations.ipInventory.ipcategory,
    },
  ];

  const section1FieldsRow3: FormFieldConfig[] = [
    {
      name: "productname",
      label: "Product Name",
      type: "text",
      disabled: true,
      placeholder: "Enter product name as per label...",
    },
    {
      name: "batchlotno",
      label: "Batch / Lot No.",
      type: "text",
      disabled: true,
      placeholder: "Enter batch number...",
    },
    {
      name: "dosageform",
      label: "Dosage Form",
      type: "text",
      disabled: true,
      placeholder: "e.g. Tablet, Capsule",
    },
    {
      name: "strength",
      label: "Strength",
      type: "text",
      disabled: true,
      placeholder: "e.g. 50mg",
    },
    {
      name: "storagecondition",
      label: "Required Storage Condition",
      type: "select",
      disabled: true,
      options: IP_STORAGE_CONDITION_OPTIONS,
      placeholder: "Select condition...",
    },
  ];

  const section2Fields: FormFieldConfig[] = [
    {
      name: "receivedqty",
      label: "Total Quantity Received",
      type: "number",
      placeholder: "e.g. 500",
      disabled: true,
    },
    {
      name: "retentionqty",
      label: "Quantity Allocated for Retention",
      type: "number",
      required: true,
      placeholder: "e.g. 50",
      disabled: !selectedStudyCode,
      rules: {
        ...validations.drugRetention.retentionqty,
        validate: (value: number) => {
          if (value <= 0) return "Quantity must be greater than 0";
          if (value > Number(receivedqty)) return "Cannot exceed available quantity";
          return true;
        },
      },
    },
    {
      name: "retentiontype",
      label: "Retention Type",
      type: "select",
      required: true,
      disabled: !selectedStudyCode,
      options: RETENTION_TYPE_OPTIONS,
      placeholder: "Select type...",
      rules: validations.drugRetention.retentiontype,
    },
  ];

  const section4Fields: FormFieldConfig[] = [
    {
      name: "retentionstartdate",
      label: "Retention Start Date",
      type: "date",
      required: true,
      disabled: !selectedStudyCode,
      rules: {
        ...validations.drugRetention.retentionstartdate,
        validate: (value: string) => {
          if (!value) return "Start date is required";
          return true;
        },
      },
      optionsProps: {
        minDate: editId
          ? (parseLocalDate(ipQuery?.data?.data[0]?.createddate) ?? undefined)
          : createddate
            ? (parseLocalDate(createddate) ?? undefined)
            : undefined,
      }
    },
    {
      name: "retentionexpirydate",
      label: "Retention Expiry Date",
      type: "date",
      required: true,
      disabled: !selectedStudyCode,
      placeholder: "Select expiry date",
      rules: {
        validate: (value: string) => {
          if (!value) return "Expiry date is required";
          const valDate = parseLocalDate(value);
          const start = parseLocalDate(startDate);
          if (start && valDate && valDate <= start) {
            return "Expiry date must be after start date";
          }
          return true;
        }
      },
      optionsProps: {
        minDate: (() => {
          const d = parseLocalDate(retentionstartdate);
          if (d) {
            d.setDate(d.getDate() + 1);
            return d;
          }
          return undefined;
        })(),
      }
    },
    {
      name: "retentionperiod",
      label: "Retention Period",
      type: "text",
      disabled: true,
      placeholder: "Auto-calculated",
    },
  ];

  const onSubmit = (data: any) => {
    const payload = {
      ipid: ipid,
      retentiontype: data.retentiontype,
      ipquantity: data.receivedqty,
      retentionquantity: data.retentionqty,
      retentionstartdate: data.retentionstartdate,
      retentionexpirydate: data.retentionexpirydate,
      retentionperiod: data.retentionperiod,
    };
    if (editId) {
      setIsUpdateModalOpen(true);
    } else {
      InsertMutation.mutate(payload, {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res.message);
          navigate(RETENTION_PATHS.LIST);
        },
        onError: (err: any) => showToastMessage("error", err),
      });
    }
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    const formData = watch();
    const payload = {
      retentiontype: formData.retentiontype,
      ipquantity: formData.receivedqty,
      retentionquantity: formData.retentionqty,
      retentionstartdate: formData.retentionstartdate,
      retentionexpirydate: formData.retentionexpirydate,
      retentionperiod: formData.retentionperiod,
      retentionid: editId,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: (res: any) => {
        showToastMessage("success", res.message);
        setIsUpdateModalOpen(false);
        navigate(RETENTION_PATHS.LIST);
      },
      onError: (err: any) => showToastMessage("error", err),
    });
  };

  if (editId && (selectQuery.isLoading || selectQuery.isFetching)) return <FullPageLoader />;

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Study Mismatch Warning */}
          <GlobalStudyMismatchWarning
            globalStudy={globalStudy}
            studyOptions={studyOptions}
            isLoaded={!studySelectAllQuery.isLoading}
          />

          <SectionCard
            index={1}
            icon="ri-microscope-line"
            title="Study & Product Details"
            subtitle="Select the study protocol and enter product information."
          >
            <CommonForm fields={section1FieldsRow1} control={control} errors={errors} columns={3} />
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow2} control={control} errors={errors} columns={1} />
            </div>
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow3} control={control} errors={errors} columns={3} />
            </div>
          </SectionCard>

          <SectionCard
            index={2}
            icon="ri-archive-line"
            title="Retention Details"
            subtitle="Archival quantities and retention category."
          >
            <CommonForm fields={section2Fields} control={control} errors={errors} columns={3} />
          </SectionCard>

          <SectionCard
            index={4}
            icon="ri-calendar-event-line"
            title="Retention Timeline"
            subtitle="Archival period and automated expiry tracking."
          >
            <CommonForm fields={section4Fields} control={control} errors={errors} columns={3} />
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button
                type="button"
                color="dark"
                outline
                onClick={() => navigate(RETENTION_PATHS.LIST)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                outline
                disabled={InsertMutation.isPending || UpdateMutation.isPending}
              >
                {InsertMutation.isPending && <Spinner size="sm" className="me-2" />}
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </form>

        {isUpdateModalOpen && (
          <Reason
            isOpen={isUpdateModalOpen}
            toggle={() => setIsUpdateModalOpen(false)}
            click={handleUpdate}
            action="Update"
            id={editId ? Number(editId) : null}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddRetention;
